import { api } from "@lib/orpc-client";
import { RedemptionOption } from "../types/redemption";
import { useCallback, useEffect, useState } from "react";
import { SupportedEWallet } from "./useSupportedEWallet";
import * as Sentry from "@sentry/react";

export type AddEWalletParams = {
  provider: string;
  phone_number: string;
  label: string;
};

export type EditEWalletParams = AddEWalletParams & {
  uuid: string;
};

export type EWallet = Awaited<
  ReturnType<typeof api.walletV2.ewallet.list>
>[number];

const useEWalletManagement = (fetchEWallets: boolean = true) => {
  const [localLoading, setLocalLoading] = useState(true);

  const addEWallet = async (params: AddEWalletParams) => {
    try {
      return await api.walletV2.ewallet.add(params);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const editEWallet = async (params: EditEWalletParams) => {
    try {
      return await api.walletV2.ewallet.update({
        uuid: params.uuid,
        provider: params.provider,
        phone_number: params.phone_number,
        label: params.label,
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const removeEWallet = async (ewallet_uuid: string) => {
    try {
      return await api.walletV2.ewallet.delete({ uuid: ewallet_uuid });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const validateEWallet = async (ewallet_uuid: string) => {
    try {
      return await api.walletV2.ewallet.validation({ uuid: ewallet_uuid });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const [ewallets, setEWallets] = useState<EWallet[]>([]);
  const [ewalletAsRedemptionOption, setEWalletAsRedemptionOption] = useState<
    RedemptionOption[]
  >([]);
  const [supportedEWallets, setSupportedEWallets] = useState<
    SupportedEWallet[]
  >([]);

  const getDataBase = async () => {
    try {
      setLocalLoading(true);
      const ewallets = await api.walletV2.ewallet.list({});
      const supportedEWallets = await api.walletV2.ewallet.supportedEwallets(
        {}
      );
      const mappedEWallets: RedemptionOption[] = ewallets.map((ewallet) => {
        return {
          id: ewallet.uuid,
          iconUrl: ewallet.iconUrl,
          accountNumber: ewallet.phone_number,
          provider: ewallet.provider,
          name: ewallet.label,
          isValid: ewallet.status === "VALIDATED",
        };
      });

      setSupportedEWallets(supportedEWallets);
      setEWalletAsRedemptionOption(mappedEWallets);
      setEWallets(ewallets);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setLocalLoading(false);
    }
  };

  const getData = useCallback(() => {
    getDataBase();
  }, []);

  useEffect(() => {
    if (!fetchEWallets) return;
    getData();
  }, [fetchEWallets]);

  const isLoading = localLoading;

  return {
    addEWallet,
    editEWallet,
    removeEWallet,
    validateEWallet,
    ewallets,
    ewalletAsRedemptionOption,
    supportedEWallets,
    isLoading,
    reload: getDataBase,
  };
};

export default useEWalletManagement;
